import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBox, faBoxCheck } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, LoadingModule, SectionDivider,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useCampaignItem from '@hooks/useCampaignItem'
import useCampaignItemsAndOrders from '@hooks/useCampaignItemsAndOrders'
import useOrder, { generateUrls } from '@hooks/useOrder'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import PageHeader from '@components/PageHeader'

import type { CampaignModel } from '@models/types'

import OrderItemTasks from '@sections/Client/packs/Campaign/screens/Orders/components/OrderItemTasks'
import OrderScreenFooter from '@sections/Client/packs/Campaign/screens/Orders/components/OrderScreenFooter'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import NotificationModuleLocation from '@components/NotificationModuleLocation'
import type { PageContextPayload } from '../../index'

import OrderNewItemButton from '../../../../components/OrderNewItemButton'
import useApprovedItemSelector from '../../../../hooks/useApprovedItemSelector'

import ApprovedOrder from '../ApprovedOrder'
import ApprovedOrderTools from '../ApprovedOrderTools'
import CampaignItem from '../CampaignItem'
import OrdersBlankState from '../OrdersBlankState'

type QueryParams = { cacheKey?: string, redirect?: string}

interface PageContentProps {
  campaign: CampaignModel,
}

const PageContent = (props: PageContentProps) => {
  const { campaign } = props
  const { address } = campaign || {}

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { cacheKey, redirect }: QueryParams = parsePermittedQueryParams(getQueryParamsFromHash(), [
    'cacheKey', 'redirect',
  ])

  // Orders
  const campaignItemsAndOrdersPayload = useCampaignItemsAndOrders({
    campaign,
    performHttpRequests: true,
    requestOptions: {
      cache_key: cacheKey,
      order_by: 'id desc',
      ...defaultRequestOptions.artwork,
      ...defaultRequestOptions.artworkPage,
      ...defaultRequestOptions.artworkReview,
      ...defaultRequestOptions.attachment,
      ...defaultRequestOptions.order,
      ...defaultRequestOptions.orderItem,
      ...defaultRequestOptions.orderItemTask,
      ...defaultRequestOptions.publication,
      ...defaultRequestOptions.publicationEdition,
    },
  })

  const {
    canGenerateSchedule,
    filteredCampaignItems,
    hasFilteredCampaignItems,
    groupedOrders: {
      approved: approvedOrders,
    },
    loading,
    orderLimitReached,
  } = campaignItemsAndOrdersPayload

  const hasOrders = !!approvedOrders.length

  // Campaign Item
  const campaignItemPayload = useCampaignItem({ campaign_id: campaign.id })
  const {
    callbacks: {
      createOrEditCampaignItem,
    },
  } = campaignItemPayload

  // Approved Items
  const approvedItemsPayload = useApprovedItemSelector({
    campaign,
    campaignItems: filteredCampaignItems,
    orders: approvedOrders,
  })

  const {
    callbacks: {
      createInvoiceFromItems,
      selectItem,
      toggleAll,
    },
    hasSelectedItems,
    selectedCampaignItemIds,
    selectedOrderIds,
  } = approvedItemsPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const { callbacks: { showGenerateCampaignScheduleModal } } = pageContext

  const { approvedCampaignOrdersUrl, pendingCampaignOrdersUrl } = generateUrls({ campaign_id: campaign?.id })

  const orderPayload = useOrder({ agency_id: campaign.agency_id, campaign_id: campaign.id })
  const { callbacks: { addNewOrder } } = orderPayload

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Approved Orders</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <>
            <OrderNewItemButton
              callbacks={{ addNewOrder }}
              campaignId={campaign.id}
              orderLimitReached={orderLimitReached}
            />

            {redirect && (
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="secondaryUtility"
                href={redirect}
                marginLeft="medium"
                size="medium"
                width="auto"
              >
                Back
              </Button>
            )}
          </>
        )}
        activeTabBarItemKey="approved"
        nestedNavigation
        tabBarItems={[
          {
            href: pendingCampaignOrdersUrl,
            icon: faBox,
            key: 'pending',
            title: 'Pending',
          },
          {
            href: approvedCampaignOrdersUrl,
            icon: faBoxCheck,
            key: 'approved',
            title: 'Approved',
          },
        ]}
        title="Orders"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.orders.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasOrders && <OrdersBlankState />}

            {/* Approved Orders */}
            {!loading && !!approvedOrders.length && (
              <>
                <SectionDivider boxProps={{ marginBottom: 'large' }}>Approved</SectionDivider>
                {approvedOrders.map(order => (
                  <ApprovedOrder
                    callbacks={{
                      selectOrderId: () => selectItem(order),
                    }}
                    // @ts-expect-error TS2339
                    isSelected={selectedOrderIds.includes(order.id)}
                    // @ts-expect-error TS2339
                    key={order.id}
                    order={order}
                  />
                ))}
              </>
            )}
            {/* End Approved Orders */}

            {/* Campaign Items */}
            {!loading && hasFilteredCampaignItems && (
              <>
                <SectionDivider boxProps={{ marginBottom: 'large' }}>Custom</SectionDivider>
                {filteredCampaignItems.map(campaignItem => (
                  <CampaignItem
                    callbacks={{
                      selectCampaignItemId: () => selectItem(campaignItem),
                    }}
                    campaignItem={campaignItem}
                    isSelected={selectedCampaignItemIds.includes(campaignItem.id)}
                    key={campaignItem.id}
                  />
                ))}
              </>
            )}
            {/* End Campaign Items */}

            {/* Pending Orders */}
            <>
              <SectionDivider boxProps={{ marginBottom: 'large' }}>Pending</SectionDivider>
              <OrderScreenFooter href={pendingCampaignOrdersUrl}>
                <FontAwesomeIcon icon={faBox} /> Pending Orders
              </OrderScreenFooter>
            </>
            {/* End Pending Orders */}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.orders.sidebar" />

            <ApprovedOrderTools
              callbacks={{
                addCustomItem: createOrEditCampaignItem,
                createInvoiceFromItems,
                showGenerateCampaignScheduleModal,
                toggleAllItems: toggleAll,
              }}
              canGenerateSchedule={canGenerateSchedule}
              hasSelectedItems={hasSelectedItems}
            />

            <OrderItemTasks campaign={campaign} />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
